import { Box, Typography } from '@mui/material';
import { Formik } from 'formik';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomStepper from 'components/shared/CustomStepper/CustomStepper';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { apiDealTypes, DealsDetailsType, DealType, PIPELINE_MAP } from 'model/deals.model';

import RoutePaths from 'routes/RoutePaths';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';

import DealsAddProductFormField from './components/DealsAddProductFormField';
import DealsPriceBookField from './components/DealsPriceBookField';

import CustomCard from 'components/shared/CustomCard/CustomCard';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import { modalReducer } from 'reducers/modalReducer';
import Section from './DynamicFormSection';
import { getDealTypeKey } from './DynamicFormSection/utils';
import AdditionalInformation from './staticFormSections/AdditionalInformation';
import AttributionTracking from './staticFormSections/AttributionTracking';
import FinancialDualRevenue from './staticFormSections/FinancialDualRevenue';
import FinancialInformation from './staticFormSections/FinancialInformation';
import ProjectInformation from './staticFormSections/ProjectInformation';

export interface FormValues {
  pipelineType: DealType | '';
  priceBookId: string;
  dealDetails: DealsDetailsType;
}

const getStepConfig = (
  isContentProduction: boolean,
  values: FormValues,
  props: any,
  isEdit: boolean,
) => {
  const { setFieldValue, id, createdDealId } = props;
  const pipeline = getDealTypeKey(values.pipelineType)!;

  const steps = [
    {
      label: 'Opportunity Information',
      content: <Section dealType={pipeline} />,
    },
    {
      label: isContentProduction ? 'Financial Information (Dual Revenue)' : 'Financial Information',
      content: isContentProduction ? <FinancialDualRevenue /> : <FinancialInformation />,
    },
    {
      label: 'Financial Information',
      content: <FinancialInformation />,
      hide: !isContentProduction,
    },
    {
      label: 'Project Information',
      content: <ProjectInformation />,
      hide: !isContentProduction,
    },
    {
      label: 'Attribution & Tracking',
      content: <AttributionTracking />,
    },
    {
      label: 'Additional Information',
      content: <AdditionalInformation />,
    },
    {
      label: 'Price book',
      content: (
        <DealsPriceBookField
          value={values.priceBookId}
          handleInputChange={(field = 'priceBookId', val: any) => setFieldValue(field, val)}
          isEdit={isEdit}
        />
      ),
    },
    {
      label: 'Products',
      content: (
        <DealsAddProductFormField
          opportunityId={id || createdDealId || ''}
          value={{
            pricebookId: values.priceBookId,
            products: values.dealDetails?.products,
          }}
          handleInputChange={(field, val, index) => {
            if (index !== undefined && field !== 'products') {
              const currentProducts = values.dealDetails?.products || [];
              const updatedProducts = [...currentProducts];
              updatedProducts[index] = { ...updatedProducts[index], [field]: val };
              setFieldValue('dealDetails.products', updatedProducts);
            } else {
              setFieldValue(`dealDetails.${field}`, val);
            }
          }}
        />
      ),
    },
  ];

  return steps.filter((step) => !step.hide);
};

const METADATA_FIELDS = [
  'dealId',
  'id',
  'createdDate',
  'createdAt',
  'createdBy',
  'createdByName',
  'lastModifiedDate',
  'updatedAt',
];

type DealFromProps = {
  dealId?: string;
  isEdit: boolean;
  deal?: any;
  pipelineType: DealType;
};

export const DealFrom = ({ isEdit, deal, pipelineType }: DealFromProps) => {
  const { id } = useParams();
  const [createdDealId, setCreatedDealId] = useState<string | null>(null);
  const [initialFormValues, setInitialFormValues] = useState<FormValues | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const navigate = useNavigate();
  const [modalState, dispatch] = useReducer(modalReducer, {
    cancelEdit: false,
  });

  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const transformApiToForm = (dealData?: any): FormValues => {
    const apiData = dealData || {};
    let rawPipelineType = apiData.pipelineType || apiData.dealType;

    let pipelineType: DealType | '' = '';
    if (rawPipelineType) {
      if (PIPELINE_MAP[rawPipelineType as apiDealTypes]) {
        pipelineType = PIPELINE_MAP[rawPipelineType as apiDealTypes] as DealType;
      } else if (Object.values(PIPELINE_MAP).includes(rawPipelineType)) {
        pipelineType = rawPipelineType as DealType;
      } else {
        const normalizedKey = Object.keys(PIPELINE_MAP).find(
          (key) => key.toLowerCase() === rawPipelineType.toLowerCase(),
        );
        if (normalizedKey) {
          pipelineType = PIPELINE_MAP[normalizedKey as apiDealTypes] as DealType;
        } else {
          const normalizedValue = Object.values(PIPELINE_MAP).find(
            (value) => value.toLowerCase() === rawPipelineType.toLowerCase(),
          );
          pipelineType = (normalizedValue || rawPipelineType) as DealType;
        }
      }
    }

    const priceBookId = apiData.priceBookId || apiData.pricebookId || apiData.pricebook2Id || '';
    const {
      pipelineType: _,
      priceBookId: __,
      pricebookId: ___,
      pricebook2Id: ____,
      dealType: _____,
      ...dealDetails
    } = apiData;

    return {
      pipelineType: pipelineType || '',
      priceBookId: priceBookId || '',
      dealDetails: dealDetails as DealsDetailsType,
    };
  };

  const data = useMemo(() => transformApiToForm(deal), [deal]);

  const initialValues = useMemo<FormValues>(() => {
    if (isEdit) {
      return {
        pipelineType: data.pipelineType as DealType,
        dealDetails: data.dealDetails,
        priceBookId: data.priceBookId,
      };
    }
    return {
      pipelineType: pipelineType as DealType,
      priceBookId: '',
      dealDetails: {} as DealsDetailsType,
    };
  }, [isEdit, data]);

  const initialActiveStep = useMemo(() => {
    return 0;
  }, []);

  useEffect(() => {
    setActiveStep(initialActiveStep);
  }, [initialActiveStep]);

  useEffect(() => {
    if (isEdit && !initialFormValues && initialValues.pipelineType) {
      setInitialFormValues(JSON.parse(JSON.stringify(initialValues)));
    }
  }, [isEdit, initialValues.pipelineType, initialFormValues]);

  const hasFormChanged = (initial: FormValues | null, current: FormValues): boolean => {
    if (!initial) return true;

    if (initial.pipelineType !== current.pipelineType) return true;
    if (initial.priceBookId !== current.priceBookId) return true;

    const initialDetails = { ...initial.dealDetails };
    const currentDetails = { ...current.dealDetails };

    METADATA_FIELDS.forEach((field) => {
      delete initialDetails[field as keyof typeof initialDetails];
      delete currentDetails[field as keyof typeof currentDetails];
    });

    return JSON.stringify(initialDetails) !== JSON.stringify(currentDetails);
  };

  const normalizeDealType = (type: DealType | ''): string => {
    if (!type) return '';
    const entry = Object.entries(PIPELINE_MAP).find(([_, value]) => value === type);
    return entry ? entry[0] : type;
  };

  const getSelectedProducts = (products: any[]) => {
    return products.filter(
      (product: any) =>
        product?.Quantity &&
        product?.Quantity !== '' &&
        product?.Quantity !== null &&
        product?.Quantity !== undefined,
    );
  };

  const productsHaveChanged = (initialProducts: any[], currentProducts: any[]): boolean => {
    const normalizeProduct = (p: any) => ({
      PricebookEntryId: p?.PricebookEntryId || p?.id,
      Quantity: p?.Quantity,
      UnitPrice: p?.UnitPrice || p?.SalesPrice,
      ServiceDate: p?.ServiceDate || p?.Date,
    });

    const initialStr = JSON.stringify(initialProducts.map(normalizeProduct));
    const currentStr = JSON.stringify(currentProducts.map(normalizeProduct));
    return initialStr !== currentStr;
  };

  const handleProductSubmit = async (values: FormValues) => {
    const products = values.dealDetails.products || [];
    const currentDealId = id || createdDealId;

    if (!currentDealId) {
      completeOperation('create', 'Deal ID is required to add products', true);
      return;
    }

    const selectedProducts = getSelectedProducts(products);

    if (isEdit && initialFormValues) {
      const initialProducts = initialFormValues.dealDetails?.products || [];
      const initialSelectedProducts = getSelectedProducts(initialProducts);

      if (!productsHaveChanged(initialSelectedProducts, selectedProducts)) {
        completeOperation('create', 'No changes to save');
        return;
      }
    }

    try {
      startOperation('create', 'Adding products to deal...');

      const response = await DealsService.updateDeal(currentDealId, {
        products: selectedProducts.map((product: any) => ({
          ...product,
          OpportunityId: currentDealId,
        })),
      });

      completeOperation('create', 'Products added successfully');
      navigate(`${RoutePaths.Deals}/${response.dealId}`);
    } catch (err: any) {
      completeOperation('create', err?.response?.data?.message || 'Failed to save products', true);
    }
  };

  const handleDealSubmit = async (values: FormValues) => {
    const { pipelineType, priceBookId, dealDetails } = values;

    if (isEdit && id && initialFormValues && !hasFormChanged(initialFormValues, values)) {
      if (priceBookId && priceBookId !== 'none' && priceBookId !== initialValues.priceBookId) {
        setActiveStep((prev) => prev + 1);
      } else {
        navigate(`${RoutePaths.Deals}/${id}`);
      }
      return;
    }

    const { expectCloseDate, ...restDealDetails } = dealDetails;

    const updatableFields = { ...restDealDetails };

    METADATA_FIELDS.forEach((field) => {
      delete updatableFields[field as keyof typeof updatableFields];
    });

    Object.keys(updatableFields).forEach((key) => {
      const value = updatableFields[key as keyof typeof updatableFields];
      if (value === '') {
        delete updatableFields[key as keyof typeof updatableFields];
      }
    });

    const payload = {
      ...updatableFields,
      closeDate: restDealDetails.closeDate || expectCloseDate || undefined,
      pipelineType: normalizeDealType(pipelineType),
      pricebookId: priceBookId !== 'none' ? priceBookId : undefined,
    };

    const operationType = isEdit && id ? 'update' : 'create';

    try {
      startOperation(operationType, isEdit && id ? 'Updating deal...' : 'Creating deal...');

      let response;
      if (isEdit && id) {
        try {
          response = await DealsService.updateDeal(id, payload);
        } catch (dealsError) {}
      } else {
        response = await DealsService.createDeal(payload);
        if (response?.id || response?.dealId) {
          setCreatedDealId(response.id || response.dealId);
        }
      }

      const finalDealId = response.dealId || response.id || id;
      completeOperation(operationType, `Deal ${isEdit ? 'updated' : 'created'} successfully`);

      if (priceBookId && priceBookId !== 'none') {
        setActiveStep((prev) => prev + 1);
      } else {
        navigate(`${RoutePaths.Deals}/${finalDealId}`);
      }
    } catch (err: any) {
      completeOperation(
        operationType,
        err?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} deal`,
        true,
      );
    }
  };

  const isContentProduction = pipelineType === 'Content/Production';

  const onCancelEdit = () => dispatch({ type: 'OPEN', payload: 'cancelEdit' });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values) => {
        const currentSteps = getStepConfig(
          isContentProduction,
          values,
          {
            id,
            createdDealId,
          },
          isEdit,
        );

        const currentStepLabel = currentSteps[activeStep].label;
        const isLastStep = activeStep === currentSteps.length - 1;

        if (currentStepLabel === 'Price book') {
          await handleDealSubmit(values);
        } else if (isLastStep) {
          await handleProductSubmit(values);
        } else {
          setActiveStep((prev) => prev + 1);
        }
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => {
        const steps = getStepConfig(
          isContentProduction,
          values,
          {
            setFieldValue,
            id,
            createdDealId,
          },
          isEdit,
        );

        const currentStep = steps[activeStep];
        const isLastStep = activeStep === steps.length - 1;

        const isStep0Valid = () => {
          if (activeStep !== 0) return true;

          if (!values.pipelineType) {
            return false;
          }

          const dealDetails = values.dealDetails || {};
          const requiredFields = [
            'dealDetails.name',
            'dealDetails.clientName',
            'dealDetails.ownerName',
            'dealDetails.stageName',
          ];

          return requiredFields.every((fieldName) => {
            const fieldKey = fieldName.replace('dealDetails.', '');
            const value = dealDetails[fieldKey];
            return value !== undefined && value !== null && value !== '';
          });
        };

        const isContinueDisabled = !isStep0Valid();

        return (
          <>
            <Box
              sx={{
                display: 'flex',
                overflow: 'scroll',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: zeplinColor.Neutral1,
                  zIndex: 10,
                }}
              >
                <CustomStepper
                  activeStep={activeStep}
                  steps={steps.map((s) => s.label)}
                  setActiveStep={setActiveStep}
                  setCompleted={setCompleted}
                  completed={completed}
                  disabled={activeStep === 0 || false}
                />
              </Box>

              <Box sx={{ padding: '24px', height: '100%', overflow: 'scroll' }}>
                {currentStep?.content}
              </Box>

              <Box
                sx={{
                  position: 'fixed',
                  flex: 1,
                  width: 'calc(100% - 209px)',
                  bottom: '0px',
                  zIndex: '100',
                  padding: '24px',
                  backgroundColor: zeplinColor.Neutral1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  boxShadow: '0 -2px 10px #00000012',
                }}
              >
                <Box sx={{ display: 'inline-flex', gap: '24px' }}>
                  <CustomButton
                    text={'Cancel'}
                    onClick={onCancelEdit}
                    variant='outlined'
                    color='customSecondary'
                    style={{
                      borderRadius: '14px',
                      height: 56,
                      width: '180px',
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      '&.Mui-focusVisible': { backgroundColor: zeplinColor.Neutral1 },
                    }}
                    textStyle={{
                      color: zeplinColor.BasicBlack,
                    }}
                    disabled={false}
                  />
                  {activeStep > 0 && (
                    <CustomButton
                      text={'Back'}
                      onClick={() => setActiveStep((as) => as - 1)}
                      variant='outlined'
                      color='customSecondary'
                      style={{
                        borderRadius: '14px',
                        height: 56,
                        width: '180px',
                        border: `1px solid ${zeplinColor.BasicBlack}`,
                        '&.Mui-focusVisible': { backgroundColor: zeplinColor.Neutral1 },
                      }}
                      textStyle={{
                        color: zeplinColor.BasicBlack,
                      }}
                      disabled={false}
                    />
                  )}
                  <CustomButton
                    text={isLastStep ? 'Save' : 'Continue'}
                    onClick={handleSubmit}
                    style={{
                      borderRadius: '14px',
                      height: 56,
                      width: '180px',
                      '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                    }}
                    disabled={isContinueDisabled}
                    variant={'contained'}
                  />
                </Box>
              </Box>
            </Box>

            <CustomAlert
              open={alertState.status !== 'idle'}
              handleClose={() => setAlertState(initialAlertState)}
              message={getAlertContent(alertState)}
              error={alertState.status === 'error'}
            />

            <CustomModal
              handleClose={() => dispatch({ type: 'CLOSE', payload: 'cancelEdit' })}
              open={modalState.cancelEdit}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CustomCard
                  styles={{
                    borderRadius: '14px',
                    padding: '24px',
                    backgroundColor: zeplinColor.Neutral1,
                    width: '500px',
                    boxShadow: 'rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                        Discard new changes?
                      </Typography>
                      <Typography variant='body2' color={zeplinColor.BasicBlack}>
                        You will lose all changes made. Are you sure you want to continue?
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '16px' }}>
                      <Box
                        onClick={() => dispatch({ type: 'CLOSE', payload: 'cancelEdit' })}
                        sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                      >
                        <CustomCard
                          styles={{
                            borderRadius: '14px',
                            padding: '14px 32px',
                            backgroundColor: zeplinColor.Neutral1,
                            border: `1px solid ${zeplinColor.BasicBlack}`,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            flex: 1,
                          }}
                        >
                          <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                            Keep Editing
                          </Typography>
                        </CustomCard>
                      </Box>
                      <Box
                        onClick={() => {
                          dispatch({ type: 'CLOSE', payload: 'cancelEdit' });
                          navigate(-1);
                        }}
                        sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                      >
                        <CustomCard
                          styles={{
                            borderRadius: '14px',
                            padding: '14px 32px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            flex: 1,
                            bgcolor: zeplinColor.Error,
                          }}
                          aria-label='button'
                        >
                          <Typography
                            variant='buttonMedium'
                            color={zeplinColor.Neutral1}
                            sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                          >
                            Discard Changes
                          </Typography>
                        </CustomCard>
                      </Box>
                    </Box>
                  </Box>
                </CustomCard>
              </Box>
            </CustomModal>
          </>
        );
      }}
    </Formik>
  );
};
